import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, Output, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatError, MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { Workspace } from '@workspaces/models/workspaces';
import { combineLatest, map, Observable, startWith, Subscription, BehaviorSubject } from 'rxjs';
import { Template } from '@workspaces/models/templates';
import { MatOption, MatSelect } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { TemplateFetch } from 'src/app/workspaces/store/template.feature';
import { templateFeature } from '@workspaces/store/template.feature';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatProgressBar } from '@angular/material/progress-bar';
import { LoaderButtonComponent } from '../../loader-button/loader-button.component';
import { WorkspaceAuthService } from '@workspaces/utils/workspace-auth.service';

export interface CommonForm {
  name: string;
  description: string;
  templateId: string;
  templateVersion: string;
  docAiInfo: string;
}

@Component({
  selector: 'app-common-form',
  templateUrl: './common-form.component.html',
  styleUrls: ['./common-form.component.scss', '../../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInput,
    MatFormField,
    MatError,
    MatOption,
    MatLabel,
    MatSelect,
    CdkTextareaAutosize,
    MatProgressBar,
    LoaderButtonComponent,
    MatHint
  ]
})
export class CommonFormComponent implements OnInit, OnDestroy {
  @Input() loading = false;

  private readonly _workspaceData$ = new BehaviorSubject<Workspace | null>(null);

  //Don't use ControlValueAccessor as we want to keep state separated from parent and limit render check
  @Input() set initialData(value: Workspace | null) {
    if (value) {
      this.isArchived = value.is_archived;
      this._isEdit = true;
      this._workspaceData$.next(value);

      this.commonForm.patchValue({
        description: value.description,
        name: value.name,
        templateId: value.template_id,
        templateVersion: value.template_version,
        docAiInfo: value.ai_doc_info
      });
    }
  }

  private _isEdit = false;
  public isArchived = false;
  public readonly commonFormControls: Record<keyof CommonForm, AbstractControl> = {
    name: new FormControl<string>('', Validators.required),
    description: new FormControl<string | null>('', Validators.required),
    templateId: new FormControl<string | null>(null, Validators.required),
    templateVersion: new FormControl<string | null>(null, Validators.required),
    docAiInfo: new FormControl<string | null>(null, Validators.required)
  };
  public readonly commonForm = new FormGroup(this.commonFormControls);
  public uniqueTemplates$!: Observable<Template[]>;
  public availableVersions$!: Observable<Template[]>;
  private readonly sub = new Subscription();
  public readonly templateIsLoading$ = this.store.select(templateFeature.templatesIsLoading);
  public readonly templatesIsError$ = this.store.select(templateFeature.selectIsError);
  @Output() readonly save = new EventEmitter();
  public saveButtonIsActive = true;

  constructor(
    private readonly store: Store<AppState>,
    private readonly workspaceAuthService: WorkspaceAuthService
  ) {}

  ngOnInit(): void {
    this.initSubscriptions();

    const allTemplates$ = this.store.select(templateFeature.selectData);

    this.uniqueTemplates$ = allTemplates$.pipe(
      map(templates => {
        return [...new Map(templates.map(template => [template.name, template])).values()];
      })
    );

    this.availableVersions$ = combineLatest([
      allTemplates$,
      this.commonFormControls.templateId.valueChanges.pipe(startWith(this.commonFormControls.templateId.value))
    ]).pipe(
      map(([templates, currentTemplateId]) => {
        return templates.filter(template => template.id === currentTemplateId);
      })
    );

    this.store.dispatch(TemplateFetch({ show_latest: false }));
  }

  private initSubscriptions(): void {
    this.sub.add(
      combineLatest([this._workspaceData$, this.workspaceAuthService.isWorkspaceAdmin$()]).subscribe(
        ([workspace, isWorkspaceAdmin]) => {
          this.handleAccessControl(workspace, isWorkspaceAdmin);
        }
      )
    );

    if (!this._isEdit) {
      this.sub.add(
        this.commonFormControls.templateId.valueChanges.subscribe(currentTemplateId => {
          this.commonFormControls.templateVersion.reset();
          if (currentTemplateId) {
            this.commonFormControls.templateVersion.enable();
          } else {
            this.commonFormControls.templateVersion.disable();
          }
        })
      );
    }
  }

  private handleAccessControl(workspace: Workspace | null, isWorkspaceAdmin: boolean): void {
    if (!this._isEdit || !workspace) {
      this.saveButtonIsActive = true;
      return;
    }

    const isArchived = workspace.is_archived;
    const isDeleted = workspace.is_deleted;
    const isFinished = workspace.state === 'FINISHED';

    // The workspace is deleted or archived than even the admin too can't modify it.
    const isGlobalDisabled = isArchived || isDeleted;

    // The user is not a workspace admin and the workspace is in finished state than it should be disabled.
    const isRoleDisabled = !isWorkspaceAdmin && isFinished;

    // Only admin can save the form.
    this.saveButtonIsActive = isWorkspaceAdmin && !isGlobalDisabled;

    if (isGlobalDisabled || isRoleDisabled) {
      this.commonForm.disable();
    } else {
      this.commonForm.enable();
      this.commonFormControls.templateId.disable();
      this.commonFormControls.templateVersion.disable();
    }
  }

  onSubmit(): void {
    if (this.commonForm.valid) {
      this.save.emit(this.commonForm.value);
    } else {
      this.commonForm.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
